<template>
  <div class="group-page">
    <template v-if="!isLoading">
      <div v-if="queuesCards.length" style="width: 100%">
        <h2 class="group-page__title">Очереди</h2>
        <div class="group-page__list">
          <QueueCard v-for="queue in queuesCards" :key="queue.Id" :queue="queue" />
        </div>
      </div>
      <div v-else class="group-page__empty">
        <TirPmSystemMessage>
          <template #topDescImage>
            <div>
              <TirPmIconView style="width: 5rem; height: 5rem">
                <template #mainSlot>
                  <FolderOpenIcon
                    style="width: 3.5rem; height: 3.5rem; color: var(--neutral-70, #686c73)"
                  />
                </template>
              </TirPmIconView>
            </div>
          </template>
          <template #mainTitleSlot>В данной папке еще нет очередей</template>
          <template #mainSubtitleSlot>Создайте очередь, чтобы добавить задачи</template>
          <template #descriptionSlot>Задачи могут содержаться только в очередях</template>
          <template #actionsSlot>
            <TirPmPopover
              width="26.25rem"
              class="group-page__empty-popover"
              :position="PopoverPositionEnum.Bottom"
            >
              <template #default>
                <TirPmButton
                  class="group-page__empty-button"
                  :variant="TirPmButtonVariantEnum.PrimaryState"
                  :size="TirPmButtonSizeEnum.Regular"
                >
                  Создать очередь
                </TirPmButton>
              </template>
              <template #content>
                <FormCreateQueue :groupId="groupId" @onCreate="onCreateQueue" />
              </template>
            </TirPmPopover>
          </template>
        </TirPmSystemMessage>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { DeleteQueueInfoModel, QueueModel, UpdateQueueNameModel } from './models';
import { AdapterService } from './adapter';
import { FormCreateQueue, QueueCard, QueueCardtemModel } from '@/components';
import { pending } from './utils';
import { TirPmSystemMessage } from 'tir-pm-system-message';
import {
  TirPmButton,
  TirPmButtonVariantEnum,
  TirPmButtonSizeEnum,
  TirPmIconView,
  TirPmPopover,
  PopoverPositionEnum,
} from 'tir-components';
import { FolderOpenIcon } from 'tir-style-system/icons/outline';
import { QueueTasksModel } from '@/components/sidebar/models';

const adapter = new AdapterService();

const props = defineProps({
  /**ID группы */
  groupId: {
    type: Number,
    required: true,
  },
});

// Обновление очереди
const onUpdateQueue = (_queue: QueueTasksModel | UpdateQueueNameModel) => {
  const currentQueue = queues.value.find(
    (queue) => queue.Id == (_queue as UpdateQueueNameModel).QueueId,
  );
  currentQueue.Name = _queue.Name;
};

/**Загрузка */
const isLoading = ref(false);
/**Список очередей */
const queues = ref(new Array<QueueModel>());

/**Список очередей в виде карточек */
const queuesCards = computed(() =>
  queues.value.map(
    (queue) =>
      new QueueCardtemModel({
        Id: queue.Id,
        Name: queue.Name,
        TasksCount: queue.TasksCount,
        ParentGroupId: props.groupId,
      }),
  ),
);

const pendingGetQueueList = pending(async (groupId: number) => {
  const resp = await adapter.getQueueList(groupId);
  if (resp.IsSuccess) queues.value = resp.Value;
});
/**Подписаться/отписаться на/от события */
const toggleEvents = (isSubscribe: boolean) => {
  const eventFnName = isSubscribe ? 'on' : 'removeListener';

  adapter.OnUpdateQueueName[eventFnName](onUpdateQueue);
  adapter.DeleteQueueEvent[eventFnName](onDeleteQueue);
  adapter.CreateQueueEvent[eventFnName](onCreateQueue);
};

const getQueueList = async () => {
  isLoading.value = true;
  await pendingGetQueueList(props.groupId);
  isLoading.value = false;
};

watch(
  () => props.groupId,
  () => {
    getQueueList();
  },
);

/**При создании очереди из сайдбара */
const onCreateQueueFromSidebar = (e: any) => {
  const groupId = e?.detail?.GroupId as number | undefined;
  if (!groupId || groupId !== props.groupId) return;
  getQueueList();
};
/**При удалении очереди из сайдбара */
const onDeleteQueue = (queue: DeleteQueueInfoModel | string) => {
  const queueId = (queue as DeleteQueueInfoModel).QueueId;
  if (!queueId || !queues.value.some((queue) => queue.Id === queueId)) return;
  queues.value = queues.value.filter((queue) => queue.Id !== queueId);
};
/**При создании "Очереди" */
const onCreateQueue = (queue: QueueTasksModel) => {
  getQueueList();
};
/**После вмонтирования компонента */
onMounted(() => {
  getQueueList();
  toggleEvents(true);
});
/**После размонтирования компонента */
onUnmounted(() => {
  toggleEvents(false);
});
</script>

<style lang="scss" scoped>
.group-page {
  display: flex;
  box-shadow: 0rem 0rem 0.063rem 0rem rgba(0, 0, 0, 0.039),
    0rem 0.25rem 0.5rem 0rem rgba(0, 0, 0, 0.039);
  background-color: var(--neutral-0);

  padding: 1.5rem;
  border-radius: 0.5rem;

  min-height: calc(100vh - 9.375rem);

  &__list {
    display: flex;
    flex-direction: column;
    row-gap: 0.75rem;
    margin-top: 0.75rem;
  }

  &__title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    line-height: 1.75rem;
    color: var(--text-secondary);
  }

  &__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    width: 100%;
    &-popover,
    &-button {
      width: 26.25rem;
    }
  }
}
</style>
